import arrowAfter from '@/shared/assets/images/pdp/arrow-after.v1.png';
import arrowBefore from '@/shared/assets/images/pdp/arrow-before.v1.png';
import { formatDisplayPrice } from '@/shared/utils/priceFormatter';

const offerInnerStyle = {
  '--offer-arrow-after': `url(${arrowAfter})`,
  '--offer-arrow-before': `url(${arrowBefore})`,
} as React.CSSProperties;

interface ProductPriceSummaryProps {
  price: number | null;
  discountedPrice: number | null;
  discountValue: number | null;
  discountName: string | null;
  disclaimer: string;
}

export function ProductPriceSummary({
  price,
  discountedPrice,
  discountValue,
  discountName,
  disclaimer,
}: ProductPriceSummaryProps) {
  const hasDiscount = price !== null && discountedPrice !== null && price !== discountedPrice;
  const displayPrice = discountedPrice ?? price;

  return (
    <div>
      {hasDiscount ? (
        <>
          <div className="flex items-stretch px-5 py-4 md:hidden">
            <div className="flex-1">
              {displayPrice !== null && (
                <p className="font-primary text-[22px] leading-tight text-gray-900">
                  {formatDisplayPrice(displayPrice)}
                </p>
              )}
              {price !== null && (
                <p className="mt-0.5 text-sm text-gray-400 line-through">{formatDisplayPrice(price)}</p>
              )}
            </div>
            <div
              className="w-px self-center bg-gray-200"
              style={{ minHeight: '62px' }}
              aria-hidden
            />
            <div className="flex-1 self-center px-4">
              {discountValue !== null && (
                <p className="text-sm leading-snug text-[#007236]">
                  Save&nbsp;&nbsp;{formatDisplayPrice(discountValue)}
                </p>
              )}
              {discountName && (
                <p className="mt-0.5 text-sm leading-snug text-[#007236]">({discountName})</p>
              )}
            </div>
          </div>

          <div className="hidden md:block">
            {price !== null && (
              <p className="text-base leading-tight text-[#9b9b9b] line-through">
                {formatDisplayPrice(price)}
              </p>
            )}
            <div className="mt-1 flex flex-nowrap items-center gap-5">
              {displayPrice !== null && (
                <p className="shrink-0 font-primary text-[32px] font-bold leading-none text-black">
                  {formatDisplayPrice(displayPrice)}
                </p>
              )}
              {discountName && (
                <div className="inline-block min-w-0 shrink align-middle">
                  <div className="inline-block py-0.5 pl-0 pr-2.5 align-baseline">
                    <div
                      className="relative block min-h-[25px] text-sm leading-[15px] before:absolute before:inset-y-0 before:-left-1.5 before:block before:h-auto before:w-[35px] before:bg-center before:bg-no-repeat before:[background-image:var(--offer-arrow-before)] before:[background-size:auto_100%] before:content-[''] after:absolute after:inset-y-0 after:-right-[19px] after:block after:h-auto after:w-5 after:bg-no-repeat after:[background-image:var(--offer-arrow-after)] after:[background-position:0_50%] after:[background-size:auto_100%] after:content-['']"
                      style={offerInnerStyle}
                    >
                      <span className="ml-3.5 inline-flex min-h-[25px] min-w-0 items-center border-y border-[#d3d3d3] bg-[#f9f9f9] px-2.5 py-1 font-secondary text-sm leading-[15px] text-[#666]">
                        <span className="line-clamp-2 break-words text-center text-[#666]">
                          {discountName}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="px-5 py-5 text-center md:px-0 md:py-0 md:text-left">
          {displayPrice !== null && (
            <p className="font-primary text-[22px] leading-tight text-gray-900 md:text-[32px] md:font-bold md:text-black">
              {formatDisplayPrice(displayPrice)}
            </p>
          )}
        </div>
      )}

      <p className="px-5 pb-2 pt-2 text-center text-xs leading-[15px] text-gray-700 md:px-0 md:pt-2 md:text-left">
        (MRP Incl. of all taxes)
      </p>

      {disclaimer && (
        <p className="px-5 pb-3 text-center text-xs leading-[15px] text-gray-700 md:px-0 md:text-left">
          {disclaimer}
        </p>
      )}
    </div>
  );
}
